import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, CalendarClock } from "lucide-react";
import { NAV_LINKS, LOGOS } from "@/data/site";
import LeadDialog from "@/components/site/LeadDialog";
import { useLanguage } from "@/i18n/useLanguage";

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const navKeyMap = {
    "Home": "home", "Services": "services", "Solutions": "solutions",
    "Industries": "industries", "Case Studies": "caseStudies",
    "About": "about", "Contact": "contact",
  };
  const labelFor = (en) => t(`nav.${navKeyMap[en] || en.toLowerCase()}`, { defaultValue: en });

  return (
    <header
      data-testid="site-navbar"
      className={
        "fixed top-0 inset-x-0 z-50 transition-all duration-300 " +
        (scrolled ? "backdrop-blur-xl bg-[#071A2D]/70 border-b border-white/[0.06]" : "bg-transparent")
      }
    >
      <div className="container-zx flex h-20 md:h-[88px] items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={LOGOS.zolvixMark} alt="Zolvix Technologies" className="h-12 w-12 md:h-14 md:w-14 object-contain" />
          <div className="leading-tight">
            <div className="font-display text-xl md:text-[22px] font-bold text-white tracking-tight">ZOLVIX</div>
            <div className="text-[10px] md:text-[11px] font-semibold tracking-[0.28em] text-cyan">SOLVE · BUILD · SCALE</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                "px-3 py-2 text-[13.5px] font-medium rounded-md transition-colors " +
                (isActive ? "text-white" : "text-ink-200 hover:text-white")
              }
            >
              {labelFor(l.label)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LeadDialog
            source="navbar"
            titleVariant="consultation"
            trigger={
              <button type="button" className="btn-primary text-[13px] px-5 py-2.5" data-testid="nav-cta-consultation">
                <CalendarClock className="h-4 w-4" /> Schedule a Consultation <ArrowRight className="h-4 w-4" />
              </button>
            }
          />
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/[0.06] bg-[#071A2D]/95 backdrop-blur-xl"
          >
            <div className="container-zx py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    "px-3 py-3 rounded-md text-sm " +
                    (isActive ? "text-white bg-white/5" : "text-ink-200 hover:text-white hover:bg-white/5")
                  }
                >
                  {labelFor(l.label)}
                </NavLink>
              ))}
              <LeadDialog
                source="navbar_mobile"
                titleVariant="consultation"
                trigger={
                  <button type="button" className="btn-primary mt-2 w-full" data-testid="nav-mobile-cta">
                    <CalendarClock className="h-4 w-4" /> Schedule a Consultation <ArrowRight className="h-4 w-4" />
                  </button>
                }
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
